import React, { useEffect, useState } from 'react';
import { Container, Heading } from '@charlietango/ui';

import { CollectionState, ElectionType } from '../../api/api-types';
import { useParty } from '../../api/hooks/useParty';
import { useUser } from '../../api/hooks/useUser';
import DocumentTitle from '../../application/DocumentTitle';
import InitiateDeclarationViewModel from '../../components/InitiateDeclaration/types/InitiateDeclarationViewModel';
import PartyDetails, {
  PartyElectionToggle,
} from '../../components/PartyDetails/PartyDetails';
import { useElectionType, usePartyId } from '../../hooks/useSearchParams';
import { colors } from '../../styles/colors';
import { readableHeadingTextWrap } from '../../styles/textWrap';
import { FontFamily, TextVariant } from '../../styles/typography';
import LinkViewModel from '../../view-models/LinkViewModel';
import NotFound from '../NotFound/NotFound';
import NotFoundViewModel from '../NotFound/types/NotFoundViewModel';
import { PlausibleGoals, trackEvent } from '../../utils/tracking';
import Hero from '../../components/Hero/Hero';
import TextBlockViewModel from '../../view-models/TextBlockViewModel';
import { DeclarationOverviewViewModel } from '../../components/DeclarationOverview/types/DeclarationOverviewViewModel';
import Steps from '../../components/Steps/Steps';
import PartiesBanner from '../../components/PartiesBanner/PartiesBanner';
import StatusBar from '../../components/StatusBar/StatusBar';
import { StepViewModel } from '../../components/Steps/types/StepViewModel';
import { HeroViewModel } from '../../components/Hero/types/HeroViewModel';
import { rem } from '../../styles/rem';
import RichText from '../../components/RichText/RichText';
import AccordionViewModel from '../../view-models/AccordionViewModel';
import HowToSection from '../../components/HowToSection/HowToSection';
import AlertBanner from '../../components/AlertBanner/AlertBanner';
import InfoIcon from '../../icons/InfoIcon';
import StatusIcon from '../../icons/StatusIcon';
import { useDictionary } from '../../hooks/useDictionary';
import { resolveStepsContent } from '../../components/Steps/resolveStepsContent';
import { formatUrl, isLocalUrl, normalizeUrl } from '../../utils/url-helpers';
import { useRouter } from '../../application/Router';

export type PartyStatusAlertKey = 'locked' | 'approved' | 'deactivated';
const partyStatusAlertLabels: Partial<Record<PartyStatusAlertKey, string>> = {
  locked: 'Låst',
  approved: 'Godkendt',
  deactivated: 'Deaktiveret',
};

export function getPartyStatusAlertStatuses(
  partyStatus: boolean | undefined,
  collectionState: CollectionState,
): PartyStatusAlertKey[] {
  const alertBannerStatuses: PartyStatusAlertKey[] = [];

  if (
    partyStatus === false ||
    collectionState === CollectionState.deactivated
  ) {
    alertBannerStatuses.push('deactivated');
  }
  if (collectionState === CollectionState.locked) {
    alertBannerStatuses.push('locked');
  }
  if (collectionState === CollectionState.approved) {
    alertBannerStatuses.push('approved');
  }

  return alertBannerStatuses;
}

export function resolvePartyElectionHref(
  href: string,
  {
    partyId,
    pathname,
  }: {
    partyId: string;
    pathname: string;
  },
) {
  const url = normalizeUrl(href);
  if (!isLocalUrl(url)) return href;

  const params = new URLSearchParams(url.search || '');
  const targetElection = params.get('election');
  if (!targetElection || params.has('party')) return href;

  params.set('party', partyId);
  url.pathname = pathname;
  url.search = `?${params.toString()}`;
  url.hash = '#app';

  return formatUrl(url);
}

export type PartyPageProps = {
  /** Intro labels based on what elections a party is applying for */
  introLabels?: {
    dk: string;
    eu: string;
    all: string;
  };
  initiateDeclaration?: InitiateDeclarationViewModel;
  howTo?: AccordionViewModel;
  attentionAnalogue?: LinkViewModel & {
    heading?: string;
    description?: string;
  };
  notFound?: NotFoundViewModel;
  declarationOverview?: DeclarationOverviewViewModel;
  dialogPageUrl?: string;
  stepsIntro?: string;
  stepsContent?: StepViewModel[];
  heroProps?: HeroViewModel;
  euParagraph?: TextBlockViewModel;
  textForBothElectionTypesFTV?: string;
  textForBothElectionTypesEPV?: string;
  textForOnlyEPV?: string;
};

function PartyPageLoading() {
  return (
    <div
      role="status"
      aria-label="Indlæser parti"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.base.Background.White,
      }}
    >
      <div
        aria-hidden="true"
        data-testid="party-page-loading-hero"
        sx={{
          minHeight: [rem(486), rem(608), rem(300), rem(340)],
          backgroundColor: colors.components.partiHero.Background.Default,
        }}
      />
      <div
        aria-hidden="true"
        data-testid="party-page-loading-action-bar"
        sx={{
          minHeight: [rem(193), rem(88)],
          backgroundColor:
            colors.components.fixedBottom.ActionBar.Background.Default,
        }}
      />
    </div>
  );
}

function PartyPage({
  attentionAnalogue,
  initiateDeclaration,
  notFound,
  heroProps,
  howTo,
  dialogPageUrl,
  stepsIntro,
  stepsContent,
  euParagraph,
  textForBothElectionTypesFTV,
  textForBothElectionTypesEPV,
  textForOnlyEPV,
}: PartyPageProps) {
  const { user, userReady } = useUser();
  const { partyId } = usePartyId();
  const { electionType } = useElectionType();
  const { pathname } = useRouter();
  const [selectedRegion, setSelectedRegion] = useState<'dk' | 'eu'>(
    electionType === ElectionType.EuropaParlamentsvalg ? 'eu' : 'dk',
  );

  const { data: party, error } = useParty(partyId);
  const { t, exists } = useDictionary();
  const resolvedStepsContent = resolveStepsContent(stepsContent).map(
    (step) => ({
      ...step,
      image: step.image ?? heroProps?.image,
    }),
  );

  useEffect(() => {
    if (electionType === ElectionType.EuropaParlamentsvalg)
      setSelectedRegion('eu');
    else if (electionType === ElectionType.Folketingsvalg)
      setSelectedRegion('dk');
  }, [electionType]);

  useEffect(() => {
    if (party) {
      trackEvent(PlausibleGoals.Party, {
        props: party.name ? { parti: party.name } : {},
      });
    }
  }, [party]);

  if (error || (userReady && !partyId)) {
    return <NotFound {...notFound} />;
  }
  if (!party) {
    return <PartyPageLoading />;
  }

  const dkRunning =
    !!party.dk && party.dk.state !== CollectionState.noCollection;
  const euRunning =
    !!party.eu && party.eu.state !== CollectionState.noCollection;
  const dkCanStartDeclaration = party.dk?.state === CollectionState.running;
  const euCanStartDeclaration = party.eu?.state === CollectionState.running;
  const shouldShowElectionToggle = dkRunning && euRunning;

  const currentRegion: 'dk' | 'eu' =
    electionType === ElectionType.BothTypes
      ? selectedRegion
      : electionType === ElectionType.EuropaParlamentsvalg
      ? 'eu'
      : 'dk';

  const collectionState: CollectionState =
    party && party[currentRegion]
      ? (party[currentRegion]?.state as CollectionState)
      : CollectionState.noCollection;

  const shouldShowWrongElectionBanner =
    collectionState === CollectionState.noCollection &&
    (dkRunning || euRunning);

  const alertBannerStatuses = getPartyStatusAlertStatuses(
    party.status,
    collectionState,
  );

  const wrongElectionBannerMessage = shouldShowWrongElectionBanner
    ? t('partyStatus.noCollection', {
        electionType: t(`${currentRegion}Election`, { asString: true }),
        asString: true,
      })
    : undefined;

  const getHeroText = () => {
    if (dkRunning && euRunning) {
      return currentRegion === 'dk'
        ? textForBothElectionTypesFTV
        : textForBothElectionTypesEPV;
    } else if (euRunning && !dkRunning) {
      return textForOnlyEPV;
    }
    return heroProps?.intro?.text;
  };

  const resolveHeroHref = (href: string) => {
    const resolvedPartyId = party.id ?? partyId;
    if (!resolvedPartyId) return href;

    return resolvePartyElectionHref(href, {
      partyId: resolvedPartyId,
      pathname,
    });
  };

  return (
    <>
      {alertBannerStatuses.map((status) => (
        <AlertBanner
          key={status}
          variant="status"
          message={t(`partyStatus.${status}`)}
          label={
            exists(`partyStatusLabel.${status}`)
              ? t(`partyStatusLabel.${status}`)
              : partyStatusAlertLabels[status]
          }
          icon={<InfoIcon width={20} height={20} viewBox="0 0 47 47" />}
          sx={{ mb: [0, 0, 8] }}
        />
      ))}
      <Container
        as="section"
        sx={{
          maxWidth: 'unset',
          backgroundColor: colors.components.partiHero.Background.Default,
          px: [0, null, 0],
        }}
      >
        {shouldShowWrongElectionBanner && (
          <AlertBanner
            message={wrongElectionBannerMessage}
            icon={<StatusIcon width={20} height={20} />}
          />
        )}
        <Container
          as="section"
          sx={{
            pt: shouldShowWrongElectionBanner
              ? [rem(32), rem(32), rem(40)]
              : [rem(64), rem(80)],
            pb: [rem(64), rem(80), rem(20)],
            px: [rem(20), rem(20), rem(40)],
          }}
        >
          {process.env.NODE_ENV !== 'test' && (
            <DocumentTitle title={`${party.name} | Vælgererklæring`} />
          )}
          <header>
            {shouldShowElectionToggle && (
              <div sx={{ mb: rem(24) }}>
                <PartyElectionToggle
                  selectedRegion={currentRegion}
                  onSelectedRegionChange={setSelectedRegion}
                  dkRunning={dkRunning}
                  euRunning={euRunning}
                />
              </div>
            )}
            <Heading
              as="h1"
              sx={{
                fontSize: [rem(48), rem(48), rem(48), rem(80)],
                fontFamily: FontFamily.Heading,
                color: colors.base.Text.DarkGreen,
                lineHeight: [rem(56), rem(56), rem(56), rem(96)],
                fontWeight: 400,
                letterSpacing: rem(1.2),
                marginBottom: rem(40),
                '@media (max-width: 47.99em)': {
                  overflowWrap: 'anywhere',
                },
                ...readableHeadingTextWrap,
              }}
            >
              {party.name}
            </Heading>

            {party && (
              <PartyDetails
                sx={{ mb: [0, 0, 8] }}
                party={party}
                hideNoCollectionStatus={shouldShowWrongElectionBanner}
                selectedRegion={currentRegion}
                onSelectedRegionChange={
                  electionType === ElectionType.BothTypes
                    ? setSelectedRegion
                    : undefined
                }
              />
            )}
          </header>
        </Container>
      </Container>
      {userReady && (
        <div>
          <StatusBar
            user={user}
            electionType={electionType}
            partyId={party.id}
            partyName={party.name}
            collectionState={collectionState}
            model={initiateDeclaration}
            dialogPageUrl={dialogPageUrl}
            dkRunning={dkCanStartDeclaration}
            euRunning={euCanStartDeclaration}
          />
        </div>
      )}
      <Container
        as="section"
        sx={{
          pt: ['2rem', '4rem', '4.5rem'],
          pb: ['3.75rem', '4rem', '7rem'],
          px: [0, null, 0],
        }}
      >
        <div sx={{ pt: ['2rem', '4rem', '4.5rem'] }}>
          <div
            sx={{
              px: [0, rem(20), rem(40)],
              '& section': {
                px: [rem(36), 0, 0],
              },
            }}
          >
            <Hero
              user={user}
              userReady={userReady}
              resolveRichTextHref={resolveHeroHref}
              heroProps={{
                image: heroProps?.image,
                intro: {
                  title: heroProps?.intro?.title,
                  text: getHeroText(),
                },
              }}
            />
          </div>

          <div
            sx={{
              '& > section': {
                mt: [rem(80), rem(80), rem(120)],
                mb: 0,
              },
            }}
          >
            <Steps intro={stepsIntro} steps={resolvedStepsContent} />
          </div>

          {euRunning && currentRegion === 'eu' && (
            <section
              sx={{
                mt: [rem(80), rem(80), rem(120)],
                mb: [rem(80), rem(80), rem(120)],
                px: [rem(36), rem(20), rem(40)],
              }}
            >
              <Heading
                as="h2"
                variant={TextVariant.HeaderXl}
                sx={{
                  mb: rem(16),
                  ...readableHeadingTextWrap,
                }}
              >
                {euParagraph?.title}
              </Heading>

              <RichText
                variant={TextVariant.BodyLg}
                sx={{
                  fontSize: rem(20),
                  '> ul': {
                    pl: rem(20),
                  },
                }}
              >
                {euParagraph?.text}
              </RichText>
            </section>
          )}

          {howTo && (
            <div
              sx={{
                mt: [rem(80), rem(80), rem(120)],
                px: [rem(20), rem(20), rem(20)],
              }}
            >
              <HowToSection title={howTo.title} items={howTo.items} />
            </div>
          )}
        </div>
      </Container>

      <div>
        <PartiesBanner attentionAnalogue={attentionAnalogue} />
      </div>
    </>
  );
}

PartyPage.displayName = 'PartyPage';

export default PartyPage;
